import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LegalEntities = ({ onSubmit, initialData }) => {
  const { t } = useTranslation();
  const [entities, setEntities] = useState([]);
  const [errors] = useState({});

  const countries = [
    { code: 'PL', name: t('workflow.scopeBoundaries.legalEntities.countries.PL') },
    { code: 'DE', name: t('workflow.scopeBoundaries.legalEntities.countries.DE') },
    { code: 'US', name: t('workflow.scopeBoundaries.legalEntities.countries.US') },
    { code: 'GB', name: t('workflow.scopeBoundaries.legalEntities.countries.GB') },
    { code: 'FR', name: t('workflow.scopeBoundaries.legalEntities.countries.FR') },
    { code: 'IT', name: t('workflow.scopeBoundaries.legalEntities.countries.IT') },
    { code: 'ES', name: t('workflow.scopeBoundaries.legalEntities.countries.ES') },
    { code: 'NL', name: t('workflow.scopeBoundaries.legalEntities.countries.NL') },
    { code: 'BE', name: t('workflow.scopeBoundaries.legalEntities.countries.BE') },
    { code: 'AT', name: t('workflow.scopeBoundaries.legalEntities.countries.AT') }
  ];

  const consolidationMethods = [
    { value: 'full', label: t('workflow.scopeBoundaries.legalEntities.consolidationMethods.full') },
    { value: 'equity', label: t('workflow.scopeBoundaries.legalEntities.consolidationMethods.equity') },
    { value: 'proportionate', label: t('workflow.scopeBoundaries.legalEntities.consolidationMethods.proportionate') }
  ];

  const addNewEntity = useCallback(() => {
    const newEntity = {
      id: Date.now().toString(),
      legal_name: '',
      country: '',
      registration_id: '',
      consolidation_method: 'full',
      ownership_pct: '',
      external_id: '',
      is_active: true,
      created_by: 'current_user',
      created_at: new Date().toISOString()
    };
    setEntities(prev => [...prev, newEntity]);
  }, []);

  // Initialize with saved data or one empty entity
  useEffect(() => {
    if (entities.length === 0) {
      // Try to load saved data from localStorage
      const savedData = localStorage.getItem('scopeBoundaries_legalEntities');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (parsedData.entities && parsedData.entities.length > 0) {
            setEntities(parsedData.entities);
            return;
          }
        } catch (error) {
          console.error('Error loading saved data:', error);
        }
      }
      // If no saved data or error, create one empty entity
      addNewEntity();
    }
  }, [entities.length, addNewEntity]);

  const updateEntity = (id, field, value) => {
    setEntities(entities.map(entity => 
      entity.id === id ? { ...entity, [field]: value } : entity
    ));
  };

  const removeEntity = (id) => {
    if (entities.length > 1) {
      setEntities(entities.filter(entity => entity.id !== id));
    }
  };

  // Validation helpers removed; WorkflowTabView manages navigation

  // Validation can be triggered externally; keep helpers but no local navigation



  // Navigation handled by WorkflowTabView; save via onSubmit externally

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <Building2 className="h-6 w-6 text-blue-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">{t('workflow.scopeBoundaries.legalEntities.title')}</h2>
        </div>
        <p className="text-gray-600">{t('workflow.scopeBoundaries.legalEntities.description')}</p>
      </div>

      {/* Entity Forms */}
      <div className="space-y-6">
        {entities.map((entity, index) => (
          <div key={entity.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('workflow.scopeBoundaries.legalEntities.entityNumber', { number: index + 1 })}
              </h3>
              {entities.length > 1 && (
                <button
                  onClick={() => removeEntity(entity.id)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title={t('workflow.scopeBoundaries.legalEntities.removeEntity')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Legal Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.legalEntities.form.legalName')}
                </label>
                <input
                  type="text"
                  value={entity.legal_name}
                  onChange={(e) => updateEntity(entity.id, 'legal_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors[`${entity.id}_legal_name`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('workflow.scopeBoundaries.legalEntities.form.legalNamePlaceholder')}
                />
                {errors[`${entity.id}_legal_name`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`${entity.id}_legal_name`]}</p>
                )}
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.legalEntities.form.country')}
                </label>
                <select
                  value={entity.country}
                  onChange={(e) => updateEntity(entity.id, 'country', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors[`${entity.id}_country`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{t('workflow.scopeBoundaries.legalEntities.form.selectCountry')}</option>
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name} ({country.code})
                    </option>
                  ))}
                </select>
                {errors[`${entity.id}_country`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`${entity.id}_country`]}</p>
                )}
              </div>

              {/* Registration ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.legalEntities.form.registrationId')}
                </label>
                <input
                  type="text"
                  value={entity.registration_id}
                  onChange={(e) => updateEntity(entity.id, 'registration_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.scopeBoundaries.legalEntities.form.registrationIdPlaceholder')}
                />
              </div>

              {/* Consolidation Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.legalEntities.form.consolidationMethod')}
                </label>
                <select
                  value={entity.consolidation_method}
                  onChange={(e) => updateEntity(entity.id, 'consolidation_method', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {consolidationMethods.map(method => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ownership Percentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.legalEntities.form.ownershipPercentage')}
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={entity.ownership_pct}
                  onChange={(e) => updateEntity(entity.id, 'ownership_pct', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors[`${entity.id}_ownership_pct`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('workflow.scopeBoundaries.legalEntities.form.ownershipPercentagePlaceholder')}
                />
                {errors[`${entity.id}_ownership_pct`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`${entity.id}_ownership_pct`]}</p>
                )}
              </div>

              {/* External ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.legalEntities.form.externalId')}
                </label>
                <input
                  type="text"
                  value={entity.external_id}
                  onChange={(e) => updateEntity(entity.id, 'external_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.scopeBoundaries.legalEntities.form.externalIdPlaceholder')}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Helper action (not navigation) */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={addNewEntity}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('workflow.scopeBoundaries.legalEntities.addEntity')}
        </button>
      </div>
    </div>
  );
};

export default LegalEntities;
